<template>
  <intro-container>
    <div class="d-flex justify-center align-top fill-height password-reset">
      <v-snackbar
        :timeout="0"
        v-model="snackbar"
        color="error"
        top
        absolute
        centered
      >
        <p>{{ errorMessage }}</p>
        <v-icon class="snackbar-icon" @click="snackbar = false">
          $iconClose
        </v-icon>
      </v-snackbar>
      <div class="password-reset__container pt-12 mt-2">
        <div class="password-reset__form" v-if="showForgotPassword">
          <v-form
            ref="forgotPasswordForm"
            v-model="valid"
            @submit.prevent="startForgotPassword"
            lazy-validation
          >
            <h1 class="mb-6">
              {{ $t("reset_password.forgot_password.title") }}
            </h1>
            <p class="body2 mb-6">
              {{ $t("reset_password.forgot_password.description") }}
            </p>
            <v-subheader class="mb-2 mt-4 ml-0 pl-0" style="height: auto">
              {{ $t("reset_password.forgot_password.field") }}
            </v-subheader>
            <v-text-field
              v-model="email"
              :placeholder="$t('reset_password.forgot_password.field')"
              :rules="[(v) => !!v || $t('login.form.error.required')]"
              hide-details="auto"
              required
              rounded
              outlined
              filled
            />
            <niko-button
              :text="$t('reset_password.forgot_password.button')"
              width="100%"
              class="mt-10"
              type="submit"
              primary
            />
          </v-form>
        </div>
        <div class="password-reset__form" v-else-if="showEmailSent">
          <v-form
            ref="forgotPasswordSubmitForm"
            v-model="valid"
            @submit.prevent="startForgotPasswordSubmit"
            lazy-validation
          >
            <h1 class="mb-6">{{ $t("reset_password.email_sent.title") }}</h1>
            <p class="body2 mb-2">
              {{
                $t("reset_password.email_sent.description", {
                  email: email,
                })
              }}
            </p>
            <div class="d-flex justify-end">
              <div>
                <niko-text-button
                  :text="$t('reset_password.email_sent.resend')"
                  color="primary"
                  class="mx-1"
                  @onClick="resendEmail"
                />
              </div>
            </div>
            <v-subheader class="mb-2 mt-4 ml-0 pl-0" style="height: auto">
              {{ $t("reset_password.email_sent.code") }}
            </v-subheader>
            <v-text-field
              v-model="code"
              :placeholder="$t('reset_password.email_sent.code')"
              :rules="[
                (v) => !!v || $t('login.form.error.required'),
                inlineCodeMismatchError
                  ? $t('reset_password.email_sent.incorrect')
                  : true,
              ]"
              hide-details="auto"
              validate-on-blur
              required
              rounded
              outlined
              filled
            />
            <v-subheader class="mt-6 mb-2 ml-0 pl-0" style="height: auto">
              {{ $t("reset_password.email_sent.new_password") }}
            </v-subheader>
            <v-text-field
              v-model="new_password"
              autocomplete="new-password"
              :placeholder="$t('reset_password.email_sent.new_password')"
              :rules="[(v) => !!v || $t('login.form.error.required')]"
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? '$iconEyeHide' : '$iconEyeShow'"
              @click:append="showPassword = !showPassword"
              @focus="isPasswordFieldActive = true"
              @blur="isPasswordFieldActive = false"
              hide-details="auto"
              validate-on-blur
              required
              rounded
              outlined
              filled
            />
            <niko-password-error
              :password="new_password"
              :isActive="isPasswordFieldActive"
              @isError="(value) => (passwordError = value)"
            />
            <niko-button
              :text="$t('reset_password.email_sent.button')"
              width="100%"
              class="mt-10"
              type="submit"
              primary
            />
          </v-form>
        </div>
        <div class="signup__confirmed" v-else>
          <h1 class="mb-6">
            {{ $t("reset_password.success.title") }}
          </h1>
          <p class="body2 mt-4">
            {{ $t("reset_password.success.description") }}
          </p>
          <niko-button
            :text="$t('reset_password.success.button')"
            width="100%"
            class="mt-10"
            type="button"
            @onClick="onPasswordResetSuccessClick"
            primary
          />
        </div>
      </div>
    </div>
  </intro-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import NikoButton from "@/components/UI/NikoButton";
import NikoTextButton from "@/components/UI/NikoTextButton";
import NikoPasswordError from "@/components/UI/NikoPasswordError";
import IntroContainer from "@/components/IntroContainer";

export default {
  name: "password-reset",
  components: {
    NikoButton,
    NikoTextButton,
    NikoPasswordError,
    IntroContainer,
  },
  data: () => ({
    valid: true,
    snackbar: false,
    errorMessage: "",
    passwordError: false,
    showPassword: false,
    isPasswordFieldActive: false,
    inlineCodeMismatchError: false,
    email: "",
    code: "",
    new_password: "",
  }),
  computed: {
    ...mapState("authStore", ["error", "showForgotPassword", "showEmailSent"]),
  },
  methods: {
    ...mapActions("authStore", [
      "forgotPassword",
      "forgotPasswordSubmit",
      "resendConfirmationCode",
    ]),
    ...mapMutations("authStore", [
      "setError",
      "setShowForgotPassword",
      "setSignupDone",
      "setEmail",
    ]),
    startForgotPassword() {
      if (this.$refs.forgotPasswordForm.validate()) {
        this.forgotPassword({ username: this.email.toLowerCase() });
        this.snackbar = false;
      } else {
        this.snackbar = true;
        this.errorMessage = this.$i18n.t("reset_password.error.fields");
      }
    },
    startForgotPasswordSubmit() {
      if (
        this.$refs.forgotPasswordSubmitForm.validate() &&
        !this.passwordError
      ) {
        this.forgotPasswordSubmit({
          code: this.code,
          new_password: this.new_password,
        });
        this.snackbar = false;
      } else {
        this.snackbar = true;
        this.errorMessage = this.passwordError
          ? this.$i18n.t("reset_password.error.password_requirements")
          : this.$i18n.t("reset_password.error.fields");
      }
    },
    resendEmail() {
      this.forgotPassword({ username: this.email.toLowerCase() });
    },
    onPasswordResetSuccessClick() {
      this.setShowForgotPassword(true);
      this.$router.push("/login");
    },
    resetErrors() {
      if (this.snackbar || this.error) {
        this.setError(null);
        this.snackbar = false;
        this.inlineCodeMismatchError = false;
      }
    },
  },
  watch: {
    error(value) {
      if (
        value.message ===
        "Cannot reset password for the user as there is no registered/verified email or phone_number"
      ) {
        this.setSignupDone(true);
        this.resendConfirmationCode(this.email.toLowerCase());
        this.setEmail(this.email.toLowerCase());
        this.$router.push("/signup");
      } else {
        this.snackbar = !!value;
        if (value) {
          this.errorMessage = value.message;
          value.code === "CodeMismatchException" &&
            (this.inlineCodeMismatchError = true);
        }
      }
    },
    email() {
      this.resetErrors();
    },
    code() {
      this.resetErrors();
    },
    new_password() {
      this.resetErrors();
    },
  },
};
</script>

<style lang="scss">
.password-reset {
  position: relative;

  &__container {
    flex-basis: 304px;
  }
}

.v-text-field--enclosed .v-input__append-inner {
  margin-top: 8px;
}

.v-input.error--text:before {
  content: "";
  height: calc(100% + 20px);
  width: 4px;
  background: var(--v-error-base);
  position: absolute;
  left: -12px;
  top: -24px;
}

.v-text-field__details {
  padding-left: 0 !important;
}

.snackbar-icon {
  width: 16px !important;
  height: 16px;
}
</style>
